import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { saveParsingTemplate, type ParsingTemplate } from '@/lib/vendorInvoiceParsing';
import { TestTube, Save, Info, Sparkles } from 'lucide-react';

interface ParsingTemplateBuilderProps {
  template: ParsingTemplate;
  onUpdate: (template: ParsingTemplate) => void;
  onTest: () => void;
}

export const ParsingTemplateBuilder = ({
  template,
  onUpdate,
  onTest,
}: ParsingTemplateBuilderProps) => {
  const { toast } = useToast();
  const [localTemplate, setLocalTemplate] = useState<ParsingTemplate>(template);
  const [saving, setSaving] = useState(false);

  const handleFieldMappingChange = (
    field: keyof ParsingTemplate['field_mappings'],
    key: string,
    value: string
  ) => {
    setLocalTemplate((prev) => ({
      ...prev,
      field_mappings: {
        ...prev.field_mappings,
        [field]: {
          ...(prev.field_mappings[field] as any),
          [key]: value,
        },
      },
    }));
  };

  const handleLineItemColumnChange = (
    column: string,
    key: string,
    value: number | string
  ) => {
    setLocalTemplate((prev) => ({
      ...prev,
      field_mappings: {
        ...prev.field_mappings,
        line_items: {
          ...prev.field_mappings.line_items,
          columns: {
            ...prev.field_mappings.line_items?.columns,
            [column]: {
              ...(prev.field_mappings.line_items?.columns?.[column as keyof typeof prev.field_mappings.line_items.columns] as any),
              [key]: value,
            },
          },
        },
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveParsingTemplate(localTemplate);
      onUpdate(localTemplate);
      toast({
        title: 'Success',
        description: 'Template saved successfully',
      });
    } catch (error: any) {
      console.error('Error saving template:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save template',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Parsing Template</CardTitle>
              <CardDescription>
                Template for {localTemplate.file_type} files • 
                Confidence: {(localTemplate.confidence_score * 100).toFixed(1)}% • 
                Trained on {localTemplate.training_samples} samples
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onTest}>
                <TestTube className="h-4 w-4 mr-2" />
                Test Template
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Total Amount Mapping */}
          {localTemplate.field_mappings.total_amount && (
            <div className="space-y-2">
              <Label>Total Amount</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Location</Label>
                  <Input
                    value={localTemplate.field_mappings.total_amount.location || ''}
                    onChange={(e) =>
                      handleFieldMappingChange('total_amount', 'location', e.target.value)
                    }
                    placeholder="e.g., bottom_right, after_subtotal"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Pattern (Regex)</Label>
                  <Input
                    value={localTemplate.field_mappings.total_amount.pattern || ''}
                    onChange={(e) =>
                      handleFieldMappingChange('total_amount', 'pattern', e.target.value)
                    }
                    placeholder="e.g., \\$?([\\d,]+(?:\\.[\\d]{2})?)"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Subtotal Mapping */}
          {localTemplate.field_mappings.subtotal && (
            <div className="space-y-2">
              <Label>Subtotal</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Location</Label>
                  <Input
                    value={localTemplate.field_mappings.subtotal.location || ''}
                    onChange={(e) =>
                      handleFieldMappingChange('subtotal', 'location', e.target.value)
                    }
                    placeholder="e.g., before_tax"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Pattern (Regex)</Label>
                  <Input
                    value={localTemplate.field_mappings.subtotal.pattern || ''}
                    onChange={(e) =>
                      handleFieldMappingChange('subtotal', 'pattern', e.target.value)
                    }
                    placeholder="Regex pattern"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tax Mapping */}
          {localTemplate.field_mappings.tax && (
            <div className="space-y-2">
              <Label>Tax Amount</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Location</Label>
                  <Input
                    value={localTemplate.field_mappings.tax.location || ''}
                    onChange={(e) =>
                      handleFieldMappingChange('tax', 'location', e.target.value)
                    }
                    placeholder="e.g., near_total"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Pattern (Regex)</Label>
                  <Input
                    value={localTemplate.field_mappings.tax.pattern || ''}
                    onChange={(e) =>
                      handleFieldMappingChange('tax', 'pattern', e.target.value)
                    }
                    placeholder="e.g., (?:tax|vat)[:\\s]*\\$?([\\d,]+)"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Payment Method Mapping */}
          {localTemplate.field_mappings.payment_method && (
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Location</Label>
                  <Input
                    value={localTemplate.field_mappings.payment_method.location || ''}
                    onChange={(e) =>
                      handleFieldMappingChange('payment_method', 'location', e.target.value)
                    }
                    placeholder="e.g., footer, header"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Pattern (Regex)</Label>
                  <Input
                    value={localTemplate.field_mappings.payment_method.pattern || ''}
                    onChange={(e) =>
                      handleFieldMappingChange('payment_method', 'pattern', e.target.value)
                    }
                    placeholder="e.g., (credit|debit|cash|check)"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Line Items Mapping */}
          {localTemplate.field_mappings.line_items && (
            <div className="space-y-4">
              <Label>Line Items Table</Label>
              {localTemplate.field_mappings.line_items.table_location && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Table Location</Label>
                  <Input
                    value={localTemplate.field_mappings.line_items.table_location}
                    onChange={(e) =>
                      setLocalTemplate((prev) => ({
                        ...prev,
                        field_mappings: {
                          ...prev.field_mappings,
                          line_items: {
                            ...prev.field_mappings.line_items!,
                            table_location: e.target.value,
                          },
                        },
                      }))
                    }
                    placeholder="e.g., main_table, after_header"
                  />
                </div>
              )}

              {localTemplate.field_mappings.line_items?.columns && (
                <div className="space-y-3">
                  <Label className="text-xs text-muted-foreground">Column Mappings</Label>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(localTemplate.field_mappings.line_items.columns).map(
                      ([column, mapping]) => (
                        <div key={column} className="space-y-2">
                          <Label className="text-xs capitalize">{column.replace('_', ' ')}</Label>
                          <div className="flex gap-2">
                            <Input
                              type="number"
                              value={mapping.index}
                              onChange={(e) =>
                                handleLineItemColumnChange(column, 'index', parseInt(e.target.value) || 0)
                              }
                              placeholder="Column index"
                              className="w-24"
                            />
                            <Input
                              value={mapping.pattern || ''}
                              onChange={(e) =>
                                handleLineItemColumnChange(column, 'pattern', e.target.value)
                              }
                              placeholder="Pattern (optional)"
                              className="flex-1"
                            />
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <p className="font-semibold mb-1">How to use this template:</p>
              <ul className="text-xs space-y-1 list-disc list-inside">
                <li>Location: Describes where to find the field (e.g., "bottom_right", "after_subtotal")</li>
                <li>Pattern: Regex pattern to extract the value from text</li>
                <li>For CSV/Excel: Column index indicates which column contains the field</li>
                <li>Test the template to see how well it performs on other invoices</li>
              </ul>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
};

